import { useIntl } from '@weenat/wintl';
import { differenceBy } from 'lodash-es';
import isNil from 'lodash-es/isNil';
import { useClient, useMutation, useQuery } from '../..';
import { canUser } from '../access';
import appPaths from '../appPaths';
import usePlotUniverses from './usePlotUniverses';
const PLOT_ACTIONS_IDS = [
    'view_alerts',
    // remove with redesign
    'view_settings',
    'view_plot',
    'view_history',
    'view_forecasts',
    'toggle_favorite',
    'archive',
    'rename',
    'geolocation',
    'remove',
    'create_alert',
    'open_in_new_tab'
];
const usePlotActions = ({ plotId, newTabHandler, onGeolocationActionPress, onRenameActionPress, onArchiveSuccess, onRemoveSuccess, excludedActions }) => {
    const client = useClient();
    const { t } = useIntl();
    const plotRequester = client.plots.get(plotId);
    const plotRequest = useQuery(plotRequester, { enabled: !isNil(plotId) });
    const plot = plotRequest.data;
    const { universe } = usePlotUniverses(plot);
    const keysToInvalidate = [
        client.plots.getPage(0).invalidationKey,
        client.orgs.get(plot?.organizationId).invalidationKey
    ];
    const [updatePlot, updatePlotRequest] = useMutation(client.plots.update(plot?.id), {
        keysToInvalidate
    });
    const [archivePlot, archivePlotRequest] = useMutation(client.plots.archive(plot?.id), {
        keysToInvalidate,
        onSuccess: onArchiveSuccess
    });
    const [setFavoriteMutation, setFavoriteRequest] = useMutation(client.plots.setFavorite(plot?.id));
    const [unsetFavoriteMutation, unsetFavoriteRequest] = useMutation(client.plots.unsetFavorite(plot?.id));
    const [deletePlot, deletePlotRequest] = useMutation(client.plots.delete(plot?.id), {
        keysToInvalidate,
        onSuccess: onRemoveSuccess
    });
    const toggleArchivedStateLabel = t(`actions.${plot?.archived ? 'unarchive' : 'archive'}`);
    const toggleArchivedStateRequest = plot?.archived ? updatePlotRequest : archivePlotRequest;
    const toggleArchivedStateIcon = plot?.archived ? 'Unarchive' : 'Archive';
    const toggleFavoriteState = plot?.isFavorite ? unsetFavoriteMutation : setFavoriteMutation;
    const toggleFavoriteStateLabel = t(`actions.${plot?.isFavorite ? 'remove_from_favorite' : 'add_to_favorite'}`);
    const toggleFavoriteStateRequest = plot?.isFavorite ? unsetFavoriteRequest : setFavoriteRequest;
    const groups = [
        {
            id: 'access',
            actions: []
        },
        {
            id: 'edition',
            actions: []
        },
        {
            id: 'local_actions',
            actions: []
        },
        {
            id: 'deletion',
            actions: []
        }
    ];
    if (!isNil(plot)) {
        const canAccessViewActions = canUser('view', 'plots', { plot }) && !plot.archived;
        const canAccessEditionActions = canUser('edit', 'plots', { plot }) && !plot.archived;
        const canDeletePlot = canUser('delete', 'plots', { plot });
        const canArchivePlot = canUser('archive', 'plots', { plot });
        if (canAccessViewActions) {
            groups[0].actions.push({
                id: 'view_plot',
                label: t('models.plot.actions.view_plot'),
                to: appPaths.plotsMap.plotSummary(plot.organizationId, plot.id),
                universe,
                iconName: 'Preview'
            }, {
                id: 'view_history',
                label: t('actions.view_history'),
                to: appPaths.plotsMap.plotHistory(plot.organizationId, plot.id),
                universe,
                iconName: 'History'
            }, {
                id: 'view_forecasts',
                label: t('actions.view_forecasts'),
                to: appPaths.plotsMap.plotForecast(plot.organizationId, plot.id),
                universe,
                iconName: 'Weather'
            }, {
                id: 'view_alerts',
                label: t('actions.view_alerts', { capitalize: true }),
                to: appPaths.plotsMap.plotAlerts(plot.organizationId, plot.id),
                iconName: 'Notifications'
            }, {
                id: 'create_alert',
                label: t('alerts.create', { capitalize: true }),
                to: appPaths.alerts.create,
                search: { plotId: plot.id },
                iconName: 'NotificationAdd'
            });
            groups[1].actions.push({
                id: 'toggle_favorite',
                label: toggleFavoriteStateLabel,
                onPress: async () => {
                    await toggleFavoriteState();
                },
                state: toggleFavoriteStateRequest,
                iconName: 'Star'
            });
            if (canAccessEditionActions) {
                // remove with redesign
                groups[1].actions.push({
                    id: 'view_settings',
                    label: t('titles.settings'),
                    to: appPaths.admin.plotSettings(plot.organizationId, plot.id),
                    iconName: 'SettingsCog'
                });
                if (!isNil(onRenameActionPress)) {
                    groups[1].actions.push({
                        id: 'rename',
                        label: t('models.plot.actions.rename', { capitalize: true }),
                        onPress: () => {
                            onRenameActionPress();
                        },
                        iconName: 'Edit'
                    });
                }
            }
            if (!isNil(onGeolocationActionPress)) {
                groups[2].actions.push({
                    id: 'geolocation',
                    label: t(!isNil(plot.location) ? 'models.plot.actions.locate' : 'errors.no_gps_coordinates_found'),
                    onPress: () => {
                        onGeolocationActionPress();
                    },
                    iconName: 'LocationPin',
                    isDisabled: isNil(plot.location)
                });
            }
            if (!isNil(newTabHandler)) {
                groups[2].actions.push({
                    id: 'open_in_new_tab',
                    label: t('actions.open_in_new_tab'),
                    onPress: newTabHandler,
                    iconName: 'OpenInNew'
                });
            }
        }
        if (canArchivePlot) {
            groups[3].actions.push({
                id: 'archive',
                label: toggleArchivedStateLabel,
                onPress: async () => {
                    if (plot.archived) {
                        await updatePlot({ archived: false });
                    }
                    else {
                        await archivePlot();
                    }
                },
                state: toggleArchivedStateRequest,
                iconName: toggleArchivedStateIcon
            });
        }
        if (canDeletePlot) {
            groups[3].actions.push({
                id: 'remove',
                label: t('actions.delete'),
                onPress: async () => {
                    await deletePlot();
                },
                state: deletePlotRequest,
                iconName: 'Bin',
                isDanger: true,
                needConfirmation: true
            });
        }
    }
    if (!isNil(excludedActions)) {
        const excludedActionsIds = [];
        for (const excludedAction of excludedActions.values()) {
            excludedActionsIds.push({ id: excludedAction });
        }
        for (const group of groups) {
            group.actions = differenceBy(group.actions, excludedActionsIds, 'id');
        }
    }
    return groups;
};
export default usePlotActions;
